import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { create } from '@bufbuild/protobuf';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SlideOver, SlideOverContent } from '@/components/ui/slide-over';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoaderIcon, PencilIcon, PlusIcon, TrashIcon } from '@/components/icons';
import {
  Skill,
  SkillGroup,
  SkillGroupClass,
  SkillGroupClassSchema,
  SkillGroupSchema,
} from '@/proto/agentplatform/v1/skill_pb';
import { cortexApi } from '@/api/cortex.api';
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';
import { formatTimestamp } from '@/utils/utils';

type Tab = 'class' | 'group';

type ClassFormState = {
  name: string;
  description: string;
};

type GroupFormState = {
  name: string;
  description: string;
  classId: string;
  classParamsText: string;
};

// SkillGroup.skills 的 value 为版本号；0 表示 latest（动态跟随最新版本）
type SkillVersionMap = Record<string, number>;
type SkillVersionOption = {
  value: number;
  label: string;
  isLatest: boolean;
  isStable: boolean;
};

const EMPTY_CLASS_FORM: ClassFormState = {
  name: '',
  description: '',
};

const EMPTY_GROUP_FORM: GroupFormState = {
  name: '',
  description: '',
  classId: '',
  classParamsText: '{}',
};

function parseStringMapJson(raw: string): Record<string, string> {
  const parsed = JSON.parse(raw.trim() || '{}');
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error('classParams 必须是 JSON 对象');
  }
  return Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, String(v)]));
}

export default function SkillsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>(() =>
    searchParams.get('tab') === 'group' ? 'group' : 'class'
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [deleteState, setDeleteState] = useState<{ type: Tab; id: string } | null>(null);
  const [formError, setFormError] = useState('');
  const handledCreateIntentRef = useRef<string>('');

  const [editingClass, setEditingClass] = useState<SkillGroupClass | null>(null);
  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState<ClassFormState>(EMPTY_CLASS_FORM);
  const [classSelectedSkillsMap, setClassSelectedSkillsMap] = useState<SkillVersionMap>({});
  const [classLeftChecked, setClassLeftChecked] = useState<string[]>([]);
  const [classRightChecked, setClassRightChecked] = useState<string[]>([]);
  const [classSkillKeyword, setClassSkillKeyword] = useState('');

  const [editingGroup, setEditingGroup] = useState<SkillGroup | null>(null);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [groupForm, setGroupForm] = useState<GroupFormState>(EMPTY_GROUP_FORM);
  const [groupSelectedSkillsMap, setGroupSelectedSkillsMap] = useState<SkillVersionMap>({});
  const [groupLeftChecked, setGroupLeftChecked] = useState<string[]>([]);
  const [groupRightChecked, setGroupRightChecked] = useState<string[]>([]);
  const [groupSkillKeyword, setGroupSkillKeyword] = useState('');

  const classQuery = useQuery({
    queryKey: ['skillgroup-classes', page, pageSize, keyword],
    queryFn: () =>
      cortexApi.listSkillGroupClasses(
        create(ListRequestSchema, { page, pageSize, fuzzy: { name: keyword.trim() } })
      ),
  });

  const groupQuery = useQuery({
    queryKey: ['skillgroups', page, pageSize, keyword],
    queryFn: () =>
      cortexApi.listSkillGroups(
        create(ListRequestSchema, { page, pageSize, fuzzy: { name: keyword.trim() } })
      ),
  });

  const {
    data: skillsData,
    isLoading: skillsLoading,
    refetch: refetchSkills,
  } = useQuery({
    queryKey: ['skills-options-for-skillgroups'],
    enabled: showClassForm || showGroupForm,
    queryFn: () =>
      cortexApi.listSkills({
        page: 1,
        pageSize: 100,
        keyword: '',
        withVersions: false,
      }),
  });

  const allSkills = useMemo(() => skillsData?.data || [], [skillsData]);
  const skillMap = useMemo(() => new Map(allSkills.map((item) => [item.name, item])), [allSkills]);
  const skillIdMap = useMemo(() => new Map(allSkills.map((item) => [item.id, item])), [allSkills]);
  const normalizeSkillVersionMapToIDs = (source: SkillVersionMap): SkillVersionMap => {
    return Object.fromEntries(
      Object.entries(source).map(([skillKey, version]) => {
        const normalizedID = skillMap.get(skillKey)?.id || skillKey;
        return [normalizedID, version];
      })
    );
  };
  const classSelectedSkillNames = useMemo(
    () => Object.keys(classSelectedSkillsMap),
    [classSelectedSkillsMap]
  );
  const groupSelectedSkillNames = useMemo(
    () => Object.keys(groupSelectedSkillsMap),
    [groupSelectedSkillsMap]
  );
  const classSelectedSkills = useMemo(
    () =>
      classSelectedSkillNames.map((key) => {
        const matched = skillMap.get(key) || skillIdMap.get(key);
        if (matched) return matched;
        return {
          id: key,
          name: key,
          description: '',
        } as Skill;
      }),
    [classSelectedSkillNames, skillMap, skillIdMap]
  );
  const classAvailableSkills = useMemo(() => {
    const selectedKeySet = new Set(classSelectedSkillNames);
    const kw = classSkillKeyword.trim().toLowerCase();
    return allSkills.filter((item) => {
      if (selectedKeySet.has(item.name) || selectedKeySet.has(item.id)) return false;
      if (!kw) return true;
      return (
        item.name.toLowerCase().includes(kw) || (item.description || '').toLowerCase().includes(kw)
      );
    });
  }, [classSelectedSkillNames, classSkillKeyword, allSkills]);

  const groupSelectedSkills = useMemo(
    () =>
      groupSelectedSkillNames.map((key) => {
        const matched = skillMap.get(key) || skillIdMap.get(key);
        if (matched) return matched;
        return {
          id: key,
          name: key,
          description: '',
        } as Skill;
      }),
    [groupSelectedSkillNames, skillMap, skillIdMap]
  );
  const groupAvailableSkills = useMemo(() => {
    const selectedKeySet = new Set(groupSelectedSkillNames);
    const kw = groupSkillKeyword.trim().toLowerCase();
    return allSkills.filter((item) => {
      if (selectedKeySet.has(item.name) || selectedKeySet.has(item.id)) return false;
      if (!kw) return true;
      return (
        item.name.toLowerCase().includes(kw) || (item.description || '').toLowerCase().includes(kw)
      );
    });
  }, [groupSelectedSkillNames, groupSkillKeyword, allSkills]);

  const selectedVersionSkillNames = useMemo(() => {
    const merged = new Set<string>([...classSelectedSkillNames, ...groupSelectedSkillNames]);
    return Array.from(merged).sort();
  }, [classSelectedSkillNames, groupSelectedSkillNames]);

  const { data: skillVersionOptionsMap = {} } = useQuery({
    queryKey: ['skill-version-options', selectedVersionSkillNames],
    enabled: (showClassForm || showGroupForm) && selectedVersionSkillNames.length > 0,
    queryFn: async () => {
      const entries = await Promise.all(
        selectedVersionSkillNames.map(async (skillName) => {
          const skill = skillMap.get(skillName);
          if (!skill?.id) {
            return [skillName, []] as const;
          }
          try {
            const resp = await cortexApi.listSkillVersions(skill.id);
            const options: SkillVersionOption[] = (resp.versions || [])
              .map((version) => {
                const code = Number(version.versionCode);
                const tags = [
                  version.isLatest ? 'latest' : '',
                  version.isStable ? 'stable' : '',
                ].filter(Boolean);
                const suffix = tags.length > 0 ? ` (${tags.join(', ')})` : '';
                return {
                  value: code,
                  label: `v${code}${suffix}`,
                  isLatest: !!version.isLatest,
                  isStable: !!version.isStable,
                };
              })
              .sort((a, b) => b.value - a.value);
            return [skillName, options] as const;
          } catch {
            return [skillName, []] as const;
          }
        })
      );
      return Object.fromEntries(entries) as Record<string, SkillVersionOption[]>;
    },
  });

  const getSkillVersionOptions = useCallback(
    (skillName: string, selectedVersion?: number): SkillVersionOption[] => {
      const base = skillVersionOptionsMap[skillName] || [];
      if (selectedVersion === undefined || base.some((item) => item.value === selectedVersion)) {
        return base;
      }
      const label = selectedVersion === 0 ? 'latest' : `v${selectedVersion}`;
      return [
        ...base,
        { value: selectedVersion, label, isLatest: selectedVersion === 0, isStable: false },
      ].sort((a, b) => b.value - a.value);
    },
    [skillVersionOptionsMap]
  );

  const getDefaultSkillVersion = useCallback(
    (skillName: string): number => {
      const options = getSkillVersionOptions(skillName);
      return options.find((item) => item.isLatest)?.value ?? options[0]?.value ?? 0;
    },
    [getSkillVersionOptions]
  );

  // Normalize skill ID keys to name keys when skillsData loads (for editing existing groups/classes)
  useEffect(() => {
    if (!showClassForm) return;
    setClassSelectedSkillsMap((prev) => {
      let changed = false;
      const next: SkillVersionMap = {};
      for (const [key, version] of Object.entries(prev)) {
        const skillById = skillIdMap.get(key);
        if (skillById) {
          next[skillById.name] = version;
          changed = true;
        } else {
          next[key] = version;
        }
      }
      return changed ? next : prev;
    });
  }, [showClassForm, skillIdMap]);

  useEffect(() => {
    if (!showGroupForm) return;
    setGroupSelectedSkillsMap((prev) => {
      let changed = false;
      const next: SkillVersionMap = {};
      for (const [key, version] of Object.entries(prev)) {
        const skillById = skillIdMap.get(key);
        if (skillById) {
          next[skillById.name] = version;
          changed = true;
        } else {
          next[key] = version;
        }
      }
      return changed ? next : prev;
    });
  }, [showGroupForm, skillIdMap]);

  useEffect(() => {
    if (!showClassForm) return;
    setClassSelectedSkillsMap((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const skillName of classSelectedSkillNames) {
        if (!(skillName in next)) continue;
        if (next[skillName] === 0) {
          const preferred = getDefaultSkillVersion(skillName);
          if (preferred !== 0) {
            next[skillName] = preferred;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [showClassForm, classSelectedSkillNames, skillVersionOptionsMap, getDefaultSkillVersion]);

  useEffect(() => {
    if (!showGroupForm) return;
    setGroupSelectedSkillsMap((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const skillName of groupSelectedSkillNames) {
        if (!(skillName in next)) continue;
        if (next[skillName] === 0) {
          const preferred = getDefaultSkillVersion(skillName);
          if (preferred !== 0) {
            next[skillName] = preferred;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [showGroupForm, groupSelectedSkillNames, skillVersionOptionsMap, getDefaultSkillVersion]);

  const createClassMutation = useMutation({
    mutationFn: (payload: SkillGroupClass) => cortexApi.createSkillGroupClass(payload),
    onSuccess: () => {
      setShowClassForm(false);
      setEditingClass(null);
      setClassForm(EMPTY_CLASS_FORM);
      setClassSelectedSkillsMap({});
      setClassLeftChecked([]);
      setClassRightChecked([]);
      setClassSkillKeyword('');
      queryClient.invalidateQueries({ queryKey: ['skillgroup-classes'] });
    },
  });

  const updateClassMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SkillGroupClass }) =>
      cortexApi.updateSkillGroupClass(id, payload),
    onSuccess: () => {
      setShowClassForm(false);
      setEditingClass(null);
      setClassForm(EMPTY_CLASS_FORM);
      setClassSelectedSkillsMap({});
      setClassLeftChecked([]);
      setClassRightChecked([]);
      setClassSkillKeyword('');
      queryClient.invalidateQueries({ queryKey: ['skillgroup-classes'] });
    },
  });

  const createGroupMutation = useMutation({
    mutationFn: (payload: SkillGroup) => cortexApi.createSkillGroup(payload),
    onSuccess: () => {
      setShowGroupForm(false);
      setEditingGroup(null);
      setGroupForm(EMPTY_GROUP_FORM);
      setGroupSelectedSkillsMap({});
      setGroupLeftChecked([]);
      setGroupRightChecked([]);
      setGroupSkillKeyword('');
      queryClient.invalidateQueries({ queryKey: ['skillgroups'] });
    },
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SkillGroup }) =>
      cortexApi.updateSkillGroup(id, payload),
    onSuccess: () => {
      setShowGroupForm(false);
      setEditingGroup(null);
      setGroupForm(EMPTY_GROUP_FORM);
      setGroupSelectedSkillsMap({});
      setGroupLeftChecked([]);
      setGroupRightChecked([]);
      setGroupSkillKeyword('');
      queryClient.invalidateQueries({ queryKey: ['skillgroups'] });
    },
  });

  const deleteClassMutation = useMutation({
    mutationFn: (id: string) => cortexApi.deleteSkillGroupClass(id),
    onSuccess: () => {
      setDeleteState(null);
      queryClient.invalidateQueries({ queryKey: ['skillgroup-classes'] });
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: string) => cortexApi.deleteSkillGroup(id),
    onSuccess: () => {
      setDeleteState(null);
      queryClient.invalidateQueries({ queryKey: ['skillgroups'] });
    },
  });

  const activeData = tab === 'class' ? classQuery.data : groupQuery.data;
  const isLoading = tab === 'class' ? classQuery.isLoading : groupQuery.isLoading;
  const isError = tab === 'class' ? classQuery.isError : groupQuery.isError;
  const total = activeData?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const resetPageFilter = (nextTab: Tab) => {
    setTab(nextTab);
    setPage(1);
    setKeyword('');
    setSearchParams({ tab: nextTab });
  };

  useEffect(() => {
    const nextTab = searchParams.get('tab');
    if (nextTab === 'class' || nextTab === 'group') {
      setTab(nextTab);
    }
  }, [searchParams]);

  useEffect(() => {
    const createFlag = searchParams.get('create');
    if (createFlag !== '1') return;
    const targetTab = searchParams.get('tab') === 'group' ? 'group' : 'class';
    const classId = searchParams.get('classId') || '';
    const intentKey = `${targetTab}|${classId}|${createFlag}`;
    if (handledCreateIntentRef.current === intentKey) return;
    handledCreateIntentRef.current = intentKey;

    setTab(targetTab);
    setPage(1);
    setKeyword('');
    setFormError('');
    if (targetTab === 'class') {
      setEditingClass(null);
      setClassForm(EMPTY_CLASS_FORM);
      setClassSelectedSkillsMap({});
      setClassLeftChecked([]);
      setClassRightChecked([]);
      setClassSkillKeyword('');
      setShowClassForm(true);
      refetchSkills();
      return;
    }
    setEditingGroup(null);
    setGroupForm({
      ...EMPTY_GROUP_FORM,
      classId,
    });
    setGroupSelectedSkillsMap({});
    setGroupLeftChecked([]);
    setGroupRightChecked([]);
    setGroupSkillKeyword('');
    setShowGroupForm(true);
    refetchSkills();
  }, [searchParams, refetchSkills]);

  const toggleClassLeftChecked = (name: string) => {
    setClassLeftChecked((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };
  const toggleClassRightChecked = (name: string) => {
    setClassRightChecked((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };
  const addClassSelectedSkills = () => {
    if (classLeftChecked.length === 0) return;
    setClassSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const name of classLeftChecked) {
        if (!next[name]) next[name] = getDefaultSkillVersion(name);
      }
      return next;
    });
    setClassLeftChecked([]);
  };
  const addAllClassVisibleSkills = () => {
    if (classAvailableSkills.length === 0) return;
    setClassSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const item of classAvailableSkills) {
        if (!next[item.name]) next[item.name] = getDefaultSkillVersion(item.name);
      }
      return next;
    });
    setClassLeftChecked([]);
  };
  const removeClassSelectedSkills = () => {
    if (classRightChecked.length === 0) return;
    const removed = new Set(classRightChecked);
    setClassSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const name of removed) delete next[name];
      return next;
    });
    setClassRightChecked([]);
  };
  const removeAllClassSkills = () => {
    setClassSelectedSkillsMap({});
    setClassRightChecked([]);
  };
  const setClassSkillVersion = (name: string, version: number) => {
    setClassSelectedSkillsMap((prev) => ({
      ...prev,
      [name]: version,
    }));
  };

  const toggleGroupLeftChecked = (name: string) => {
    setGroupLeftChecked((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };
  const toggleGroupRightChecked = (name: string) => {
    setGroupRightChecked((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };
  const addGroupSelectedSkills = () => {
    if (groupLeftChecked.length === 0) return;
    setGroupSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const name of groupLeftChecked) {
        if (!next[name]) next[name] = getDefaultSkillVersion(name);
      }
      return next;
    });
    setGroupLeftChecked([]);
  };
  const addAllGroupVisibleSkills = () => {
    if (groupAvailableSkills.length === 0) return;
    setGroupSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const item of groupAvailableSkills) {
        if (!next[item.name]) next[item.name] = getDefaultSkillVersion(item.name);
      }
      return next;
    });
    setGroupLeftChecked([]);
  };
  const removeGroupSelectedSkills = () => {
    if (groupRightChecked.length === 0) return;
    const removed = new Set(groupRightChecked);
    setGroupSelectedSkillsMap((prev) => {
      const next = { ...prev };
      for (const name of removed) delete next[name];
      return next;
    });
    setGroupRightChecked([]);
  };
  const removeAllGroupSkills = () => {
    setGroupSelectedSkillsMap({});
    setGroupRightChecked([]);
  };
  const setGroupSkillVersion = (name: string, version: number) => {
    setGroupSelectedSkillsMap((prev) => ({
      ...prev,
      [name]: version,
    }));
  };

  const submitClass = () => {
    try {
      setFormError('');
      const skillsByID = normalizeSkillVersionMapToIDs(classSelectedSkillsMap);
      const payload = create(SkillGroupClassSchema, {
        name: classForm.name.trim(),
        description: classForm.description.trim(),
        template: create(SkillGroupSchema, {
          name: classForm.name.trim(),
          description: classForm.description.trim(),
          provider: editingClass?.template?.provider || 'default',
          skills: skillsByID,
        }),
      });
      if (editingClass) {
        updateClassMutation.mutate({ id: editingClass.id, payload });
      } else {
        createClassMutation.mutate(payload);
      }
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '表单数据不合法');
    }
  };

  const submitGroup = () => {
    try {
      setFormError('');
      const skillsByID = normalizeSkillVersionMapToIDs(groupSelectedSkillsMap);
      const payload = create(SkillGroupSchema, {
        name: groupForm.name.trim(),
        description: groupForm.description.trim(),
        classId: groupForm.classId.trim(),
        provider: editingGroup?.provider || 'default',
        classParams: parseStringMapJson(groupForm.classParamsText),
        skills: skillsByID,
      });
      if (editingGroup) {
        updateGroupMutation.mutate({ id: editingGroup.id, payload });
      } else {
        createGroupMutation.mutate(payload);
      }
    } catch (e) {
      setFormError(e instanceof Error ? e.message : '表单数据不合法');
    }
  };

  return (
    <div className="min-h-screen bg-muted/20">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">技能组管理</h1>
            <p className="text-sm text-muted-foreground mt-1">
              按“技能组模版/技能组”两种视角管理资源。
            </p>
          </div>
        </div>

        <div className="bg-background rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b flex items-center justify-between gap-4">
            <div className="flex items-center gap-1 p-1 bg-muted rounded-lg">
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  tab === 'class'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() => resetPageFilter('class')}
              >
                技能组模版
              </button>
              <button
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  tab === 'group'
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground'
                }`}
                onClick={() => resetPageFilter('group')}
              >
                技能组
              </button>
            </div>
            <div className="flex items-center gap-2">
              <Input
                className="w-56"
                placeholder="按名称搜索"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
              />
              <Button
                variant="outline"
                onClick={() => (tab === 'class' ? classQuery.refetch() : groupQuery.refetch())}
              >
                刷新
              </Button>
              <Button
                onClick={() => {
                  setFormError('');
                  if (tab === 'class') {
                    setEditingClass(null);
                    setClassForm(EMPTY_CLASS_FORM);
                    setClassSelectedSkillsMap({});
                    setClassLeftChecked([]);
                    setClassRightChecked([]);
                    setClassSkillKeyword('');
                    setShowClassForm(true);
                    refetchSkills();
                  } else {
                    setEditingGroup(null);
                    setGroupForm(EMPTY_GROUP_FORM);
                    setGroupSelectedSkillsMap({});
                    setGroupLeftChecked([]);
                    setGroupRightChecked([]);
                    setGroupSkillKeyword('');
                    setShowGroupForm(true);
                    refetchSkills();
                  }
                }}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                新建
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : isError ? (
            <div className="p-8 text-sm text-destructive">加载失败，请重试。</div>
          ) : tab === 'class' ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>描述</TableHead>
                  <TableHead>技能数</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead className="w-28">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(classQuery.data?.data || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      暂无技能组模版
                    </TableCell>
                  </TableRow>
                ) : (
                  (classQuery.data?.data || []).map((item) => (
                    <TableRow
                      key={item.id}
                      className="hover:bg-muted/20 cursor-pointer"
                      onClick={() => navigate(`/plugins/skills/group-classes/${item.id}`)}
                    >
                      <TableCell>
                        <button
                          type="button"
                          className="font-medium text-primary hover:text-primary/80 text-left"
                          onClick={() => navigate(`/plugins/skills/group-classes/${item.id}`)}
                        >
                          {item.name}
                        </button>
                        <div className="text-xs text-muted-foreground">ID: {item.id}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.description || '-'}
                      </TableCell>
                      <TableCell>{Object.keys(item.template?.skills || {}).length}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTimestamp(item.updatedAt)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingClass(item);
                              setClassForm({
                                name: item.name,
                                description: item.description,
                              });
                              setClassSelectedSkillsMap(
                                Object.fromEntries(
                                  Object.entries(item.template?.skills || {}).map(
                                    ([name, version]) => [name, Number(version) || 0]
                                  )
                                )
                              );
                              setClassLeftChecked([]);
                              setClassRightChecked([]);
                              setClassSkillKeyword('');
                              setFormError('');
                              setShowClassForm(true);
                              refetchSkills();
                            }}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteState({ type: 'class', id: item.id })}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名称</TableHead>
                  <TableHead>描述</TableHead>
                  <TableHead>模版ID</TableHead>
                  <TableHead>技能数</TableHead>
                  <TableHead>更新时间</TableHead>
                  <TableHead className="w-28">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(groupQuery.data?.data || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      暂无技能组
                    </TableCell>
                  </TableRow>
                ) : (
                  (groupQuery.data?.data || []).map((item) => (
                    <TableRow
                      key={item.id}
                      className="hover:bg-muted/20 cursor-pointer"
                      onClick={() => navigate(`/plugins/skills/groups/${item.id}`)}
                    >
                      <TableCell>
                        <button
                          type="button"
                          className="font-medium text-primary hover:text-primary/80 text-left"
                          onClick={() => navigate(`/plugins/skills/groups/${item.id}`)}
                        >
                          {item.name}
                        </button>
                        <div className="text-xs text-muted-foreground">ID: {item.id}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.description || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-xs" onClick={(e) => e.stopPropagation()}>
                        {item.classId ? (
                          <button
                            type="button"
                            className="text-primary hover:text-primary/80"
                            onClick={() =>
                              navigate(`/plugins/skills/group-classes/${item.classId}`)
                            }
                          >
                            {item.classId}
                          </button>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{Object.keys(item.skills || {}).length}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTimestamp(item.updatedAt)}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingGroup(item);
                              setGroupForm({
                                name: item.name,
                                description: item.description,
                                classId: item.classId,
                                classParamsText: JSON.stringify(item.classParams || {}, null, 2),
                              });
                              setGroupSelectedSkillsMap(
                                Object.fromEntries(
                                  Object.entries(item.skills || {}).map(([name, version]) => [
                                    name,
                                    Number(version) || 0,
                                  ])
                                )
                              );
                              setGroupLeftChecked([]);
                              setGroupRightChecked([]);
                              setGroupSkillKeyword('');
                              setFormError('');
                              setShowGroupForm(true);
                              refetchSkills();
                            }}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive"
                            onClick={() => setDeleteState({ type: 'group', id: item.id })}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}

          <div className="px-5 py-3 border-t flex items-center justify-between text-sm">
            <span className="text-muted-foreground">共 {total} 条</span>
            <div className="flex items-center gap-2">
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <Button
                variant="ghost"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                上一页
              </Button>
              <span>
                {page}/{totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                下一页
              </Button>
            </div>
          </div>
        </div>
      </div>

      <SlideOver
        open={showClassForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowClassForm(false);
            setEditingClass(null);
            setClassForm(EMPTY_CLASS_FORM);
            setClassSelectedSkillsMap({});
            setClassLeftChecked([]);
            setClassRightChecked([]);
            setClassSkillKeyword('');
            setFormError('');
          } else {
            setShowClassForm(true);
            refetchSkills();
          }
        }}
        title={editingClass ? '编辑技能组模版' : '新建技能组模版'}
        actions={
          <Button
            onClick={submitClass}
            disabled={
              !classForm.name.trim() ||
              classSelectedSkillNames.length === 0 ||
              createClassMutation.isPending ||
              updateClassMutation.isPending
            }
          >
            {(createClassMutation.isPending || updateClassMutation.isPending) && (
              <LoaderIcon className="h-4 w-4 mr-1" />
            )}
            {editingClass ? '保存' : '创建'}
          </Button>
        }
      >
        <SlideOverContent>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">名称 *</p>
            <Input
              value={classForm.name}
              onChange={(e) => setClassForm((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">描述</p>
            <textarea
              className="w-full min-h-20 rounded-md border px-3 py-2 text-sm"
              value={classForm.description}
              onChange={(e) => setClassForm((s) => ({ ...s, description: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">默认技能 *</p>
              <Button size="sm" variant="outline" onClick={() => refetchSkills()}>
                刷新技能列表
              </Button>
            </div>
            <Input
              placeholder="搜索可选技能"
              value={classSkillKeyword}
              onChange={(e) => setClassSkillKeyword(e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-start">
              <div className="rounded-md border bg-background">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  可选技能（{classAvailableSkills.length}）
                </div>
                <div className="max-h-56 overflow-auto p-1">
                  {skillsLoading ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">加载中...</p>
                  ) : classAvailableSkills.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">暂无可选技能</p>
                  ) : (
                    classAvailableSkills.map((item) => (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => toggleClassLeftChecked(item.name)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs ${
                          classLeftChecked.includes(item.name)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="font-medium break-all">{item.name}</div>
                        <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden">
                          {item.description || '-'}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="flex md:flex-col gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={addClassSelectedSkills}>
                  &gt;
                </Button>
                <Button size="sm" variant="outline" onClick={addAllClassVisibleSkills}>
                  &gt;&gt;
                </Button>
                <Button size="sm" variant="outline" onClick={removeClassSelectedSkills}>
                  &lt;
                </Button>
                <Button size="sm" variant="outline" onClick={removeAllClassSkills}>
                  &lt;&lt;
                </Button>
              </div>
              <div className="rounded-md border bg-background">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  已选技能（{classSelectedSkills.length}）
                </div>
                <div className="max-h-56 overflow-auto p-1">
                  {classSelectedSkills.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">未选择技能</p>
                  ) : (
                    classSelectedSkills.map((item) => (
                      <div
                        key={item.name}
                        className={`px-2 py-1.5 rounded text-xs ${
                          classRightChecked.includes(item.name)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleClassRightChecked(item.name)}
                          className="w-full text-left"
                        >
                          <div className="font-medium break-all">{item.name}</div>
                          <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden">
                            {item.description || '-'}
                          </div>
                        </button>
                        <div className="mt-1">
                          <select
                            value={
                              classSelectedSkillsMap[item.name] ?? getDefaultSkillVersion(item.name)
                            }
                            onChange={(e) =>
                              setClassSkillVersion(item.name, Number(e.target.value))
                            }
                            className="h-7 rounded border border-input bg-background px-2 text-xs text-foreground"
                          >
                            {getSkillVersionOptions(
                              item.name,
                              classSelectedSkillsMap[item.name]
                            ).map((option) => (
                              <option
                                key={`${item.name}-class-${option.value}`}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          {formError && <p className="text-xs text-destructive">{formError}</p>}
        </SlideOverContent>
      </SlideOver>

      <SlideOver
        open={showGroupForm}
        onOpenChange={(open) => {
          if (!open) {
            setShowGroupForm(false);
            setEditingGroup(null);
            setGroupForm(EMPTY_GROUP_FORM);
            setGroupSelectedSkillsMap({});
            setGroupLeftChecked([]);
            setGroupRightChecked([]);
            setGroupSkillKeyword('');
            setFormError('');
          } else {
            setShowGroupForm(true);
            refetchSkills();
          }
        }}
        title={editingGroup ? '编辑技能组' : '新建技能组'}
        actions={
          <Button
            onClick={submitGroup}
            disabled={
              !groupForm.name.trim() ||
              groupSelectedSkillNames.length === 0 ||
              createGroupMutation.isPending ||
              updateGroupMutation.isPending
            }
          >
            {(createGroupMutation.isPending || updateGroupMutation.isPending) && (
              <LoaderIcon className="h-4 w-4 mr-1" />
            )}
            {editingGroup ? '保存' : '创建'}
          </Button>
        }
      >
        <SlideOverContent>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">名称 *</p>
            <Input
              value={groupForm.name}
              onChange={(e) => setGroupForm((s) => ({ ...s, name: e.target.value }))}
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-sm font-medium">描述</p>
            <textarea
              className="w-full min-h-20 rounded-md border px-3 py-2 text-sm"
              value={groupForm.description}
              onChange={(e) => setGroupForm((s) => ({ ...s, description: e.target.value }))}
            />
          </div>
          {(editingGroup || groupForm.classId) && (
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Class Params (JSON)</p>
              <textarea
                className="w-full min-h-24 rounded-md border px-3 py-2 text-xs font-mono"
                value={groupForm.classParamsText}
                onChange={(e) => setGroupForm((s) => ({ ...s, classParamsText: e.target.value }))}
              />
            </div>
          )}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">技能 *</p>
              <Button size="sm" variant="outline" onClick={() => refetchSkills()}>
                刷新技能列表
              </Button>
            </div>
            <Input
              placeholder="搜索可选技能"
              value={groupSkillKeyword}
              onChange={(e) => setGroupSkillKeyword(e.target.value)}
            />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-start">
              <div className="rounded-md border bg-background">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  可选技能（{groupAvailableSkills.length}）
                </div>
                <div className="max-h-56 overflow-auto p-1">
                  {skillsLoading ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">加载中...</p>
                  ) : groupAvailableSkills.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">暂无可选技能</p>
                  ) : (
                    groupAvailableSkills.map((item) => (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => toggleGroupLeftChecked(item.name)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs ${
                          groupLeftChecked.includes(item.name)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <div className="font-medium break-all">{item.name}</div>
                        <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden">
                          {item.description || '-'}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="flex md:flex-col gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={addGroupSelectedSkills}>
                  &gt;
                </Button>
                <Button size="sm" variant="outline" onClick={addAllGroupVisibleSkills}>
                  &gt;&gt;
                </Button>
                <Button size="sm" variant="outline" onClick={removeGroupSelectedSkills}>
                  &lt;
                </Button>
                <Button size="sm" variant="outline" onClick={removeAllGroupSkills}>
                  &lt;&lt;
                </Button>
              </div>
              <div className="rounded-md border bg-background">
                <div className="px-3 py-2 text-xs text-muted-foreground border-b">
                  已选技能（{groupSelectedSkills.length}）
                </div>
                <div className="max-h-56 overflow-auto p-1">
                  {groupSelectedSkills.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-2 py-2">未选择技能</p>
                  ) : (
                    groupSelectedSkills.map((item) => (
                      <div
                        key={item.name}
                        className={`px-2 py-1.5 rounded text-xs ${
                          groupRightChecked.includes(item.name)
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleGroupRightChecked(item.name)}
                          className="w-full text-left"
                        >
                          <div className="font-medium break-all">{item.name}</div>
                          <div className="text-muted-foreground mt-0.5 whitespace-normal break-all leading-4 max-h-8 overflow-hidden">
                            {item.description || '-'}
                          </div>
                        </button>
                        <div className="mt-1">
                          <select
                            value={
                              groupSelectedSkillsMap[item.name] ?? getDefaultSkillVersion(item.name)
                            }
                            onChange={(e) =>
                              setGroupSkillVersion(item.name, Number(e.target.value))
                            }
                            className="h-7 rounded border border-input bg-background px-2 text-xs text-foreground"
                          >
                            {getSkillVersionOptions(
                              item.name,
                              groupSelectedSkillsMap[item.name]
                            ).map((option) => (
                              <option
                                key={`${item.name}-group-${option.value}`}
                                value={option.value}
                              >
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
          {formError && <p className="text-xs text-destructive">{formError}</p>}
        </SlideOverContent>
      </SlideOver>

      <AlertDialog open={!!deleteState} onOpenChange={() => setDeleteState(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除{deleteState?.type === 'class' ? '技能组模版' : '技能组'}
              吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>取消</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              disabled={deleteClassMutation.isPending || deleteGroupMutation.isPending}
              onClick={() => {
                if (!deleteState) return;
                if (deleteState.type === 'class') deleteClassMutation.mutate(deleteState.id);
                else deleteGroupMutation.mutate(deleteState.id);
              }}
            >
              {(deleteClassMutation.isPending || deleteGroupMutation.isPending) && (
                <LoaderIcon className="h-4 w-4 mr-1" />
              )}
              删除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
